import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Clock, Play, ChevronRight,
} from 'lucide-react';

const RETRIGGER = {
  apify_sync:              { path: '/auctions/refresh',                       method: 'POST' },
  transactions_backfill:   { path: '/trends/backfill',                        method: 'POST' },
  propertyfinder_refresh:  { path: '/trends/propertyfinder/refresh',          method: 'POST' },
  iris_refresh:            { path: '/iris/refresh?include_market_sync=false&include_narrate=false', method: 'POST' },
};

function StatusPill({ status }) {
  const map = {
    done:      { bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'OK' },
    completed: { bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'OK' },
    succeeded: { bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'OK' },
    ok:        { bg: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'OK' },
    running:   { bg: 'bg-sky-100 text-sky-800',         icon: RefreshCw,    label: 'Running' },
    error:     { bg: 'bg-red-100 text-red-800',         icon: XCircle,      label: 'Error' },
    failed:    { bg: 'bg-red-100 text-red-800',         icon: XCircle,      label: 'Failed' },
  };
  const cfg = map[status] || { bg: 'bg-slate-100 text-slate-600', icon: Clock, label: status || '—' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg}`}>
      <Icon size={10} className={status === 'running' ? 'animate-spin' : ''}/>
      {cfg.label}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(ts).slice(0, 16); }
}

export default function AdminSyncRuns() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/sync-runs?limit=100');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load sync runs: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retrigger = async (jobKey) => {
    const spec = RETRIGGER[jobKey];
    if (!spec) return toast.error('This job cannot be retriggered from the UI');
    setBusy((b) => ({ ...b, [jobKey]: true }));
    try {
      await api.post(spec.path);
      toast.success(`${jobKey} fired`);
      setTimeout(load, 2000);
    } catch (e) {
      toast.error('Trigger failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy((b) => ({ ...b, [jobKey]: false }));
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="admin-sync-runs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-xs text-slate-500">Admin</div>
          <h1 className="font-serif text-3xl">Nightly &amp; background runs</h1>
          <p className="text-sm text-slate-600 mt-1">
            Status of every scheduled data pipeline. Any job stuck in error &gt;24h is a hard blocker.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-sync-refresh"
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''}/>
          Refresh
        </button>
      </div>

      {!data && loading && <p className="text-slate-500">Loading…</p>}

      {data && (
        <>
          {/* PER-JOB SUMMARY CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
            {data.jobs.map((j) => {
              const isNever = j.hours_since_ok === null;
              const isCrit = j.consecutive_failures >= 2 || (j.hours_since_ok !== null && j.hours_since_ok > 48);
              const isWarn = !isCrit && j.hours_since_ok !== null && j.hours_since_ok > 24;
              const border = isCrit ? 'border-red-300 bg-red-50/60'
                : isWarn ? 'border-amber-300 bg-amber-50/60'
                : isNever ? 'border-slate-300 bg-slate-50/60'
                : 'border-emerald-300 bg-emerald-50/40';
              return (
                <motion.div
                  key={j.job}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border p-4 ${border}`}
                  data-testid={`job-card-${j.job}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-serif truncate">{j.label}</div>
                      <div className="text-[11px] text-slate-500 truncate">{j.cadence}</div>
                    </div>
                    {isCrit && <XCircle className="text-red-600 shrink-0" size={18}/>}
                    {!isCrit && isWarn && <AlertTriangle className="text-amber-600 shrink-0" size={18}/>}
                    {!isCrit && !isWarn && !isNever && <CheckCircle2 className="text-emerald-600 shrink-0" size={18}/>}
                    {isNever && <Clock className="text-slate-400 shrink-0" size={18}/>}
                  </div>

                  <div className="mt-3 space-y-1 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Last success</span>
                      <span className="font-medium">
                        {j.hours_since_ok === null ? 'Never' : `${j.hours_since_ok}h ago`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">At</span>
                      <span className="font-mono text-[10px] text-slate-700">{fmt(j.last_ok_at)}</span>
                    </div>
                    {j.consecutive_failures > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-red-700">Consecutive fails</span>
                        <span className="font-semibold text-red-700">{j.consecutive_failures}</span>
                      </div>
                    )}
                    {j.last_error_message && (
                      <div className="mt-2 rounded bg-red-100 text-red-900 px-2 py-1 text-[10px] font-mono">
                        {j.last_error_message.slice(0, 140)}
                      </div>
                    )}
                  </div>

                  {RETRIGGER[j.job] && (
                    <button
                      onClick={() => retrigger(j.job)}
                      disabled={busy[j.job]}
                      data-testid={`job-retrigger-${j.job}`}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 disabled:opacity-60"
                    >
                      {busy[j.job] ? <RefreshCw size={11} className="animate-spin"/> : <Play size={11}/>}
                      Trigger now
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* CHRONOLOGICAL RUN HISTORY */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <ChevronRight size={16}/>
              <h2 className="font-serif text-lg">Recent run history</h2>
              <span className="text-xs text-slate-500 ml-auto">{data.recent_runs.length} runs</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-runs-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2">Job</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Started</th>
                    <th className="px-4 py-2">Finished</th>
                    <th className="px-4 py-2">Message / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_runs.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-2 font-medium">{r.label}</td>
                      <td className="px-4 py-2"><StatusPill status={r.status}/></td>
                      <td className="px-4 py-2 font-mono text-[10px] text-slate-600">{fmt(r.started_at)}</td>
                      <td className="px-4 py-2 font-mono text-[10px] text-slate-600">{fmt(r.completed_at)}</td>
                      <td className="px-4 py-2 text-slate-700">
                        {r.error
                          ? <span className="text-red-700 font-mono text-[10px]">{r.error.slice(0, 120)}</span>
                          : <span className="text-slate-500">{(r.message || '—').slice(0, 100)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
